import React from 'react';
import {
  Box,
  Typography,
  Stack,
  LinearProgress,
  Card,
  CardContent,
  alpha,
  useTheme,
} from '@mui/material';
import { Timeline, AccessTime } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { getPhaseColor, getRiskColor } from './utils';

const RiskHorizonsSection = ({ latestResult }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  
  const horizons = [
    {
      phase: latestResult?.down_phase_10,
      risk: latestResult?.down_risk_10,
      horizon: '10',
      descriptionKey: 'downtrend.riskHorizons.shortTerm',
      icon: <AccessTime />,
    },
    {
      phase: latestResult?.down_phase_20,
      risk: latestResult?.down_risk_20,
      horizon: '20',
      descriptionKey: 'downtrend.riskHorizons.mediumTerm',
      icon: <Timeline />,
    },
    {
      phase: latestResult?.down_phase_60,
      risk: latestResult?.down_risk_60,
      horizon: '60',
      descriptionKey: 'downtrend.riskHorizons.longTerm',
      icon: <Timeline />,
    },
  ];

  const getPhaseGradient = (phase) => {
    if (phase === 'UP_NEUTRAL') return `linear-gradient(135deg, #16a34a 0%, #22c55e 100%)`;
    if (phase === 'EARLY_WARNING') return `linear-gradient(135deg, #ea580c 0%, #fb923c 100%)`;
    if (phase === 'CONFIRMED_DOWN') return `linear-gradient(135deg, #e11d48 0%, #f43f5e 100%)`;
    if (phase === 'SEVERE_DOWN') return `linear-gradient(135deg, #be123c 0%, #dc2626 100%)`;
    return `linear-gradient(135deg, #64748b 0%, #94a3b8 100%)`;
  };

  return (
    <Box sx={{ mb: 0 }}>
      <Card
        elevation={0}
        sx={{
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          borderRadius: 2.25,
        }}
      >
        <CardContent sx={{ p: 1.5 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
              gap: 1.25,
            }}
          >
            {horizons.map(({ phase, risk, horizon, descriptionKey, icon }) => {
              const phaseInfo = getPhaseColor(phase);
              const riskValue = risk !== null && risk !== undefined ? risk * 100 : 0;
              
              return (
                <Box key={horizon}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.1 }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 1.25,
                        background: getPhaseGradient(phase),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      {React.cloneElement(icon, { sx: { color: 'white', fontSize: 18 } })}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.58rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        {t(`downtrend.riskHorizons.horizon${horizon}`)}
                      </Typography>
                      <Typography fontWeight={800} sx={{ color: phaseInfo.color, fontSize: '0.98rem', lineHeight: 1.15 }}>
                        {t(phaseInfo.labelKey)}
                      </Typography>
                    </Box>
                  </Stack>
                  
                  {risk !== null && risk !== undefined && (
                    <Box>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.55 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.76rem' }}>
                          {t('downtrend.riskHorizons.riskScore')}
                        </Typography>
                        <Typography fontWeight={800} sx={{ fontFamily: 'monospace', color: getRiskColor(riskValue), fontSize: '0.92rem' }}>
                          {riskValue.toFixed(1)}%
                        </Typography>
                      </Stack>
                      <LinearProgress
                        variant="determinate"
                        value={riskValue}
                        sx={{
                          height: 6,
                          borderRadius: 999,
                          bgcolor: alpha(getRiskColor(riskValue), 0.1),
                          '& .MuiLinearProgress-bar': {
                            bgcolor: getRiskColor(riskValue),
                            borderRadius: 999,
                          },
                        }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem', mt: 0.55, display: 'block', lineHeight: 1.4 }}>
                        {t(descriptionKey)}
                      </Typography>
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default RiskHorizonsSection;
